import { BookOutlined, CheckCircleOutlined, SendOutlined } from '@ant-design/icons';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Modal,
  Skeleton,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDate, formatDateTime, orDash } from '../../lib/format';
import { approveDiscount, getQuotation, sendQuotation, type QuotationLine } from './api';
import { createBooking } from './bookings-api';
import { formatMoney, isPositive } from '../../lib/money';
import { QuotationEditor, quotationLinesKey } from './QuotationEditor';
import { LINE_TYPE_LABEL, QUOTATION_STATUS_LABEL, quotationTone } from './status';

const { Text, Title } = Typography;

export function QuotationDetailPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();
  // A ref, not state: Modal.confirm keeps the onOk it was opened with, so state
  // read there is what it was before anything was typed.
  const approvalReason = useRef('');

  const { data, isLoading } = useQuery({ queryKey: ['quotation', uid], queryFn: () => getQuotation(uid) });
  // A discount is saved on blur, and tapping Send is that blur. Never send
  // while the editor's save is still in flight; the server locks too (C1).
  const savingLines = useIsMutating({ mutationKey: quotationLinesKey(uid) }) > 0;

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['quotation', uid] });
    void queryClient.invalidateQueries({ queryKey: ['quotations'] });
  };

  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const send = useMutation({
    mutationFn: () => sendQuotation(uid),
    onSuccess: (result) => {
      refresh();
      // The API decides whether this went out or went for approval. Say which.
      void message.success(
        result.status === 'PENDING_APPROVAL' ? t('sales.sentForApproval') : t('sales.sent'),
      );
    },
    onError: (e: unknown) => {
      fail(e, t('sales.sendFailed'));
    },
  });

  /**
   * Booking from here rather than from a "new booking" screen is the point:
   * the customer, the quotation and the price are already on this page, so
   * nothing about them gets re-keyed into a second form.
   */
  const book = useMutation({
    mutationFn: () => createBooking({ quotationUid: uid }),
    onSuccess: (booking) => {
      refresh();
      void message.success(t('bookings.created'));
      void navigate(`/bookings/${booking.uid}`);
    },
    onError: (e: unknown) => {
      fail(e, t('bookings.createFailed'));
    },
  });

  const approve = useMutation({
    mutationFn: (reason: string) => approveDiscount(uid, reason),
    onSuccess: () => {
      refresh();
      void message.success(t('sales.approved'));
    },
    onError: (e: unknown) => {
      fail(e, t('sales.approveFailed'));
    },
  });

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  const canSend = data.status === 'DRAFT';
  // The customer has seen a price. Nothing is reserved until the deposit.
  const canBook = data.status === 'SENT' || data.status === 'ACCEPTED';
  const awaitingApproval = data.status === 'PENDING_APPROVAL';

  const confirmApproval = (): void => {
    approvalReason.current = '';
    Modal.confirm({
      title: t('sales.approveTitle'),
      content: (
        <Flex vertical gap={8}>
          <Text type="secondary">
            {t('sales.approveHelp', {
              percent: data.discountPercent,
              amount: formatMoney(data.discountTotal, data.currency),
            })}
          </Text>
          <input
            className="ant-input"
            placeholder={t('sales.approvePlaceholder')}
            onChange={(e) => {
              approvalReason.current = e.target.value;
            }}
          />
        </Flex>
      ),
      okText: t('sales.approve'),
      onOk: () => {
        const reason = approvalReason.current.trim();
        if (reason.length < 3) {
          void message.error(t('sales.approveNeedsReason'));
          return Promise.reject(new Error('reason required'));
        }
        return approve.mutateAsync(reason);
      },
    });
  };

  return (
    <DetailPage
      title={data.customerName}
      subtitle={data.quotationNo}
      tags={[
        { label: QUOTATION_STATUS_LABEL[data.status], colour: quotationTone(data.status) },
        ...(data.isExpired ? [{ label: t('sales.expired'), colour: 'error' }] : []),
      ]}
      onBack={() => {
        void navigate('/quotations');
      }}
      actions={
        <Space wrap>
          {canSend && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={send.isPending}
              disabled={savingLines}
              onClick={() => {
                if (queryClient.isMutating({ mutationKey: quotationLinesKey(uid) }) > 0) return;
                send.mutate();
              }}
            >
              {t('sales.send')}
            </Button>
          )}
          {canBook && (
            <Button
              type="primary"
              icon={<BookOutlined />}
              loading={book.isPending}
              onClick={() => {
                book.mutate();
              }}
            >
              {t('bookings.bookThis')}
            </Button>
          )}
          {awaitingApproval && (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={confirmApproval}>
              {t('sales.approve')}
            </Button>
          )}
        </Space>
      }
      tabs={[
        {
          key: 'overview',
          label: t('sales.tabs.overview'),
          content: (
            <Flex vertical gap={16}>
              {awaitingApproval && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('sales.awaitingApproval')}
                  description={t('sales.awaitingApprovalHelp', {
                    percent: data.discountPercent,
                    amount: formatMoney(data.discountTotal, data.currency),
                  })}
                />
              )}
              {canSend && data.needsDiscountApproval && (
                <Alert
                  type="info"
                  showIcon
                  message={t('sales.willNeedApproval')}
                  description={t('sales.willNeedApprovalHelp', { percent: data.discountPercent })}
                />
              )}

              {canSend ? (
                <QuotationEditor quotation={data} onSaved={refresh} />
              ) : (
                <Card
                  styles={{ body: { padding: 0 } }}
                  title={t('sales.lines')}
                  extra={
                    <Title level={5} style={{ margin: 0 }}>
                      {formatMoney(data.grandTotal, data.currency)}
                    </Title>
                  }
                >
                  <Table<QuotationLine>
                    dataSource={data.lines}
                    rowKey={(row) => row.uid}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: t('sales.fields.type'),
                        dataIndex: 'type',
                        width: 130,
                        render: (type: string) => LINE_TYPE_LABEL[type] ?? type,
                      },
                      { title: t('sales.fields.description'), dataIndex: 'description' },
                      { title: t('sales.fields.qty'), dataIndex: 'qty', width: 80, align: 'right' as const },
                      {
                        title: t('sales.fields.unitPrice'),
                        dataIndex: 'unitPrice',
                        width: 150,
                        align: 'right' as const,
                        render: (v: string) => formatMoney(v, data.currency),
                      },
                      {
                        title: t('sales.fields.discount'),
                        dataIndex: 'discountAmount',
                        width: 140,
                        align: 'right' as const,
                        render: (v: string) =>
                          isPositive(v) ? <Text type="warning">−{formatMoney(v, data.currency)}</Text> : '—',
                      },
                      {
                        title: t('sales.fields.lineTotal'),
                        dataIndex: 'lineTotal',
                        width: 160,
                        align: 'right' as const,
                        render: (v: string) => <Text strong>{formatMoney(v, data.currency)}</Text>,
                      },
                    ]}
                    summary={() => (
                      <Table.Summary>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4}>
                            <Text type="secondary">{t('sales.fields.subtotal')}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            {isPositive(data.discountTotal) ? (
                              <Text type="warning">−{formatMoney(data.discountTotal, data.currency)}</Text>
                            ) : null}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <Text strong>{formatMoney(data.grandTotal, data.currency)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </Card>
              )}

              <Card>
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label={t('sales.fields.customer')}>
                    {data.customerName}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.model')}>{orDash(data.model)}</Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.validUntil')}>
                    <Text type={data.isExpired ? 'danger' : undefined}>{formatDate(data.validUntil)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.sentAt')}>
                    {data.sentAt === null ? '—' : formatDateTime(data.sentAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.discountPercent')}>
                    {data.discountPercent}%
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.created')}>
                    {formatDateTime(data.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('fields.notes')} span={2}>
                    {orDash(data.terms)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Flex>
          ),
        },
      ]}
    />
  );
}
